/**
 * stepName — single source of truth for rendering a workflow step's label.
 *
 * Pairs with AFD spec 018-workflow-step-names, which contractually emits
 * `step.name` (non-empty string) on every step/task payload reaching AMC.
 *
 * Use `getStepName(step)` to render the visible step label. Do not render
 * the visible label from `step.name` directly — centralizing the read
 * keeps the defensive fallback consistent across surfaces and gives us
 * exactly one console.warn site when AFD breaks the contract.
 *
 * For non-visible metadata uses (e.g. the `title` tooltip on a truncated
 * label), use `getStepTitle(step)` so the attribute is omitted entirely
 * when `step.name` is missing/empty — a blank tooltip is worse than none.
 *
 * All surfaces that render a step label MUST go through this helper —
 * see spec 016-workflow-step-names.
 */

// Module-level dedup of contract-violation warnings. Keyed on the step
// object identity (WeakSet — GC-friendly, no leak). Each unique step
// object triggers at most one console.warn per session.
const _warnedSteps = new WeakSet();

/**
 * Resolve the display label for a workflow step.
 *
 * @param {object} step - A workflow step or runtime task with at minimum
 *   `name?: string` and `step_number?: string|number`. May be null/undefined
 *   when the caller couldn't locate the step at all.
 * @returns {string} A non-empty display label. Prefers `step.name` (trimmed);
 *   falls back to `"Step ${step.step_number ?? '?'}"` if name is missing,
 *   empty, or whitespace-only. Emits a one-shot `console.warn` on fallback.
 *
 *   The returned value is rendered verbatim — no casing or content rewrites.
 */
export function getStepName(step) {
  const trimmed =
    typeof step?.name === 'string' ? step.name.trim() : '';
  if (trimmed) return trimmed;

  if (step && !_warnedSteps.has(step)) {
    _warnedSteps.add(step);
    console.warn(
      '[stepName] contract violation: step.name is missing or empty; using fallback',
      { step_number: step?.step_number, id: step?.id }
    );
  }
  return `Step ${step?.step_number ?? '?'}`;
}

/**
 * Get the trimmed raw step name for use as an HTML `title` attribute.
 *
 * Returns `undefined` when `step.name` is missing, empty, or whitespace-only
 * so React omits the attribute entirely rather than rendering `title=""`
 * (which produces an empty tooltip on hover).
 *
 * @param {object} step
 * @returns {string | undefined}
 */
export function getStepTitle(step) {
  if (typeof step?.name !== 'string') return undefined;
  const trimmed = step.name.trim();
  return trimmed || undefined;
}
